import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  Calendar,
  MapPin,
  Users,
  Clock,
  ArrowRight,
  Mountain,
  TrendingUp,
  Sparkles,
  ChevronDown,
  Search,
} from "lucide-react";
import {
  useGetAdventuresMonthlyQuery,
} from "../../apiCaller/apis/getApi";
import { GetTableListAPI } from "../../apiCaller/getAPIsCaller";
import ClientLayout from "../../components/Layout/ClientLayout";
import PaginationComponent from "../../components/pagination";
import SpinningLoader from "../../components/SpinningLoader/SpinningLoader";
import NoImage from "../../assets/images/No_Image_Available.jpg";

// Helper function to format month year display
const formatMonthYear = (monthYearString) => {
  // Expected format: "January 2024" or "Jan 2024" or "2024-01"
  try {
    // Try to parse various formats
    const parts = monthYearString.trim().split(/[\s-]+/);
    
    if (parts.length >= 2) {
      const monthPart = parts[0];
      const yearPart = parts[1];
      
      // If month is a number, convert it
      if (!isNaN(monthPart)) {
        const monthNames = [
          "January", "February", "March", "April", "May", "June",
          "July", "August", "September", "October", "November", "December"
        ];
        const monthIndex = parseInt(monthPart) - 1;
        return `${monthNames[monthIndex]} ${yearPart}`;
      }
      
      // If month is already a name, just return formatted
      return `${monthPart} ${yearPart}`;
    }
    
    // Return as is if can't parse
    return monthYearString;
  } catch (error) {
    return monthYearString;
  }
};

const EventListRedesign = () => {
  const [search, setSearch] = useState({ });
  const [expandedMonths, setExpandedMonths] = useState({});
  const [activeFilter, setActiveFilter] = useState("all");

  const {
    data: adventuresByMonth,
    error: adventureError,
    isLoading: adventureIsLoading,
    isFetching: adventureIsFetching,
  } = GetTableListAPI(search, useGetAdventuresMonthlyQuery);

  const navigate = useNavigate();

  const handleClick = (slug) => {
    navigate(`/events/${slug}`);
  };

  const handlePageClick = (page) => {
    const newSearch = { ...search, page: page + 1 };
    setSearch(newSearch);
  };

  const handleSearch = (params) => {
    const newSearch = { ...search, ...params };
    setSearch(newSearch);
  };

  const toggleMonth = (monthKey) => {
    setExpandedMonths((prev) => ({
      ...prev,
      [monthKey]: !prev[monthKey],
    }));
  };

  // Initialize all months as expanded on first load
  React.useEffect(() => {
    if (adventuresByMonth && adventuresByMonth.length > 0) {
      const initialExpanded = {};
      adventuresByMonth.forEach((monthGroup, index) => {
        initialExpanded[index] = true; // All expanded by default
      });
      setExpandedMonths(initialExpanded);
    }
  }, [adventuresByMonth]);

  // Calculate total events for pagination
  const totalEvents =
    adventuresByMonth?.reduce(
      (acc, monthGroup) => acc + monthGroup.adventures.length,
      0
    ) || 0;

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 60 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const monthHeaderVariants = {
    hidden: { opacity: 0, x: -50 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.5, ease: "easeOut" },
    },
  };

  // Get difficulty color using Mamba Hikers theme
  const getDifficultyColor = (difficulty) => {
    switch (difficulty?.toLowerCase()) {
      case "beginner friendly":
        return "from-mamba-sky-blue to-blue-400";
      case "moderate":
        return "from-mamba-golden to-amber-400";
      case "challenging":
        return "from-orange-500 to-red-500";
      case "extreme":
        return "from-red-600 to-rose-700";
      default:
        return "from-mamba-navy to-slate-700";
    }
  };

  return (
    <ClientLayout>
      {/* Hero Section */}
      <section className="relative h-[500px] lg:h-[600px] overflow-hidden">
        {/* Background Image with Parallax Effect */}
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url(https://images.unsplash.com/photo-1551632811-561732d1e306?w=1920&q=90)`,
          }}
        />

        {/* Gradient Overlays - Mamba Hikers Theme */}
        <div className="absolute inset-0 bg-gradient-to-br from-mamba-navy/95 via-slate-900/85 to-mamba-navy/90" />
        <div className="absolute inset-0 bg-gradient-to-t from-mamba-navy/95 via-transparent to-transparent" />

        {/* Animated Background Elements - Mamba Hikers Theme */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 10, 0],
              opacity: [0.3, 0.5, 0.3],
            }}
            transition={{
              duration: 15,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -top-40 -right-40 w-96 h-96 bg-mamba-sky-blue/20 rounded-full blur-3xl"
          />
          <motion.div
            animate={{
              scale: [1, 1.3, 1],
              rotate: [0, -10, 0],
              opacity: [0.2, 0.4, 0.2],
            }}
            transition={{
              duration: 20,
              repeat: Infinity,
              ease: "easeInOut",
            }}
            className="absolute -bottom-40 -left-40 w-96 h-96 bg-mamba-golden/20 rounded-full blur-3xl"
          />
        </div>

        {/* Content */}
        <div className="relative z-10 h-full flex flex-col items-center justify-center px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Badge - Mamba Hikers Theme */}
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="inline-flex items-center gap-2 mb-6 px-5 py-2.5 bg-mamba-golden/20 backdrop-blur-md border border-mamba-golden/40 rounded-full"
            >
              <Sparkles className="w-5 h-5 text-mamba-golden" />
              <span className="text-sm text-white font-montserrat font-bold tracking-wide uppercase">
                Upcoming Adventures
              </span>
            </motion.div>

            {/* Title - Mamba Hikers Theme */}
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="text-5xl sm:text-6xl md:text-7xl lg:text-8xl font-montserrat font-black text-white mb-6 tracking-tight leading-none"
            >
              Explore
              <span className="block bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-sky-blue bg-clip-text text-transparent">
                Every Trail
              </span>
            </motion.h1>

            {/* Subtitle - Mamba Hikers Theme */}
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="text-xl md:text-2xl text-slate-200 max-w-3xl mx-auto mb-8 leading-relaxed font-inter"
            >
              Join the <span className="font-dancing italic text-mamba-golden">Mamba Hikers</span> community on breathtaking journeys. Every month brings new
              adventures, new challenges, and unforgettable memories.
            </motion.p>

            {/* Stats */}
            {/* <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="flex flex-wrap gap-6 justify-center"
            >
              {[
                { label: "Total Events", value: totalEvents || "0" },
                {
                  label: "Active Months",
                  value: adventuresByMonth?.length || "0",
                },
                { label: "Active Hikers", value: "2,500+" },
              ].map((stat, idx) => (
                <div
                  key={idx}
                  className="backdrop-blur-md bg-white/5 rounded-2xl border-2 border-mamba-golden/30 px-6 py-4 min-w-[140px]"
                >
                  <div className="text-3xl font-montserrat font-black bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent mb-1">
                    {stat.value}
                  </div>
                  <div className="text-slate-300 text-sm font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </motion.div> */}


          </motion.div>
        </div>

        {/* Scroll Indicator */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.8 }}
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
        >
          <motion.div
            animate={{ y: [0, 10, 0] }}
            transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
            className="text-white/60"
          >
            <ChevronDown className="w-8 h-8" />
          </motion.div>
        </motion.div>
      </section>

      {/* Main Content - Mamba Hikers Theme */}
      <section className="relative py-16 px-5 md:px-10 lg:px-32 xl:px-48 bg-gradient-to-b from-white via-mamba-bg-light to-white">
        {/* Search Bar Section */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <EventSearchBarRedesign handleSearch={handleSearch} />
        </motion.div>

        {/* Events by Month */}
        {adventureIsLoading || adventureIsFetching ? (
          <div className="min-h-[400px] flex items-center justify-center">
            <SpinningLoader isLoading={true} hasData={false} />
          </div>
        ) : adventuresByMonth && adventuresByMonth.length > 0 ? (
          <div className="space-y-16">
            {adventuresByMonth.map((monthGroup, groupIndex) => (
              <motion.div
                key={groupIndex}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-100px" }}
                variants={staggerContainer}
                className="relative"
              >
                {/* Month Header - Mamba Hikers Theme */}
                <motion.div
                  variants={monthHeaderVariants}
                  onClick={() => toggleMonth(groupIndex)}
                  className="group cursor-pointer mb-10"
                >
                  <div className="relative w-full">
                    {/* Background Accent - Mamba Colors */}
                    <div className="absolute -inset-4 bg-gradient-to-r from-mamba-golden/10 via-mamba-sky-blue/10 to-mamba-navy/10 rounded-3xl blur-xl group-hover:blur-2xl transition-all duration-500" />

                    {/* Content */}
                    <div className="relative flex items-center gap-6 bg-white rounded-2xl p-6 border-2 border-mamba-sky-blue/20 group-hover:border-mamba-golden transition-all duration-300 shadow-md group-hover:shadow-xl">
                      {/* Month Circle Badge - Mamba Theme */}
                      <motion.div
                        whileHover={{ scale: 1.1, rotate: 5 }}
                        className="flex-shrink-0 w-16 h-16 rounded-full bg-gradient-to-br from-mamba-navy to-mamba-sky-blue flex items-center justify-center shadow-lg"
                      >
                        <Calendar className="w-8 h-8 text-white" />
                      </motion.div>

                      {/* Month Text */}
                      <div className="flex-1">
                        <h2 className="text-2xl md:text-3xl font-montserrat font-bold text-mamba-navy mb-1 tracking-tight group-hover:text-mamba-sky-blue transition-colors">
                          {formatMonthYear(monthGroup.month_year)}
                        </h2>
                        <p className="text-sm text-slate-600 font-medium">
                          {monthGroup.adventures.length} adventure
                          {monthGroup.adventures.length !== 1 ? "s" : ""} scheduled
                        </p>
                      </div>

                      {/* Expand/Collapse Icon */}
                      <motion.div
                        animate={{
                          rotate: expandedMonths[groupIndex] ? 180 : 0,
                        }}
                        transition={{ duration: 0.3 }}
                        className="flex-shrink-0"
                      >
                        <div className="w-10 h-10 rounded-full bg-mamba-bg-light group-hover:bg-mamba-golden/20 flex items-center justify-center transition-colors">
                          <ChevronDown className="w-6 h-6 text-mamba-navy group-hover:text-mamba-golden transition-colors" />
                        </div>
                      </motion.div>
                    </div>

                    {/* Decorative Line - Mamba Colors */}
                    <motion.div
                      initial={{ scaleX: 0 }}
                      whileInView={{ scaleX: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.8, delay: 0.2 }}
                      className="absolute -bottom-2 left-6 right-6 h-1 bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-navy rounded-full origin-left"
                    />
                  </div>
                </motion.div>

                {/* Events Grid with Collapse Animation */}
                <AnimatePresence>
                  {expandedMonths[groupIndex] && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.5 }}
                      className="overflow-hidden"
                    >
                      <motion.div
                        variants={staggerContainer}
                        className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
                      >
                        {monthGroup.adventures.map((adventure, index) => (
                          <EventCardRedesign
                            key={adventure.id}
                            adventure={adventure}
                            index={index}
                            handleClick={handleClick}
                            fadeInUp={fadeInUp}
                            getDifficultyColor={getDifficultyColor}
                          />
                        ))}
                      </motion.div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Decorative Banner Between Months - Only after certain months */}
                {groupIndex < adventuresByMonth.length - 1 && (groupIndex + 1) % 2 === 0 && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8 }}
                    className="mt-20 mb-8"
                  >
                    <div className="relative rounded-3xl overflow-hidden">
                      {/* Background Image */}
                      <div
                        className="absolute inset-0 bg-cover bg-center"
                        style={{
                          backgroundImage: `url(https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&q=80)`,
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-r from-mamba-navy/95 via-mamba-navy/85 to-mamba-sky-blue/90" />
                      
                      {/* Animated Dots Pattern */}
                      <div className="absolute inset-0 opacity-20">
                        <div
                          className="absolute inset-0"
                          style={{
                            backgroundImage: `radial-gradient(circle at 2px 2px, rgb(212 165 116 / 0.3) 1px, transparent 0)`,
                            backgroundSize: "32px 32px",
                          }}
                        />
                      </div>

                      {/* Animated Background Elements */}
                      <motion.div
                        animate={{
                          scale: [1, 1.2, 1],
                          opacity: [0.1, 0.2, 0.1],
                        }}
                        transition={{
                          duration: 8,
                          repeat: Infinity,
                          ease: "easeInOut",
                        }}
                        className="absolute -right-20 top-1/2 -translate-y-1/2 w-64 h-64 bg-mamba-golden/30 rounded-full blur-3xl"
                      />

                      {/* Content */}
                      <div className="relative z-10 px-8 py-12 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
                        {/* Left Side - Icon & Text */}
                        <div className="flex items-center gap-6 flex-1">
                          <motion.div
                            animate={{
                              rotate: [0, 5, -5, 0],
                            }}
                            transition={{
                              duration: 6,
                              repeat: Infinity,
                              ease: "easeInOut",
                            }}
                            className="flex-shrink-0"
                          >
                            <div className="w-20 h-20 md:w-24 md:h-24 rounded-full bg-gradient-to-br from-mamba-golden to-mamba-sky-blue p-1 shadow-2xl">
                              <div className="w-full h-full rounded-full bg-mamba-navy flex items-center justify-center">
                                <Mountain className="w-10 h-10 md:w-12 md:h-12 text-mamba-golden" />
                              </div>
                            </div>
                          </motion.div>

                          <div>
                            <motion.h3
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: 0.2 }}
                              className="text-2xl md:text-4xl font-dancing italic text-mamba-golden mb-2"
                            >
                              More Adventures Await
                            </motion.h3>
                            <motion.p
                              initial={{ opacity: 0, x: -20 }}
                              whileInView={{ opacity: 1, x: 0 }}
                              viewport={{ once: true }}
                              transition={{ duration: 0.6, delay: 0.3 }}
                              className="text-base md:text-lg text-white/90 font-inter"
                            >
                              Keep scrolling to discover upcoming expeditions
                            </motion.p>
                          </div>
                        </div>

                        {/* Right Side - Animated Trail Icon */}
                        <motion.div
                          initial={{ opacity: 0, scale: 0.8 }}
                          whileInView={{ opacity: 1, scale: 1 }}
                          viewport={{ once: true }}
                          transition={{ duration: 0.6, delay: 0.4 }}
                          className="flex items-center gap-3"
                        >
                          {[0, 1, 2].map((idx) => (
                            <motion.div
                              key={idx}
                              animate={{
                                y: [0, -10, 0],
                              }}
                              transition={{
                                duration: 2,
                                repeat: Infinity,
                                delay: idx * 0.2,
                                ease: "easeInOut",
                              }}
                              className="w-3 h-3 rounded-full bg-gradient-to-br from-mamba-golden to-mamba-sky-blue shadow-lg"
                            />
                          ))}
                        </motion.div>
                      </div>

                      {/* Bottom Accent Line */}
                      <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-mamba-golden via-mamba-sky-blue to-mamba-golden" />
                    </div>
                  </motion.div>
                )}
              </motion.div>
            ))}
          </div>
        ) : (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
            className="text-center py-20"
          >
            <div className="inline-flex p-8 bg-mamba-bg-light rounded-full mb-6 border-2 border-mamba-sky-blue/20">
              <Mountain className="w-20 h-20 text-mamba-navy" />
            </div>
            <h3 className="text-3xl font-montserrat font-bold text-mamba-navy mb-3">
              No Events Found
            </h3>
            <p className="text-slate-600 text-lg max-w-md mx-auto font-inter">
              Check back soon for new adventures, or adjust your search filters
              to explore more options.
            </p>
          </motion.div>
        )}

        {/* Pagination */}
        {/* {adventuresByMonth &&
          adventuresByMonth.length > 0 &&
          totalEvents > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="mt-16"
            >
              <PaginationComponent
                pageCount={Math.ceil(totalEvents / (search.totalItems || 12))}
                totalItems={totalEvents}
                handlePageClick={handlePageClick}
              />
            </motion.div>
          )} */}
      </section>

      {/* CTA Section - Mamba Hikers Theme */}
      <section className="relative py-24 px-10 md:px-16 lg:px-48 bg-gradient-to-br from-mamba-navy via-slate-900 to-mamba-navy overflow-hidden">
        {/* Animated Background */}
        <div className="absolute inset-0 opacity-30">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: `radial-gradient(circle at 2px 2px, rgb(255 255 255 / 0.15) 1px, transparent 0)`,
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="relative z-10 text-center max-w-4xl mx-auto"
        >
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6 tracking-tight">
            Ready for Your Next{" "}
            <span className="bg-gradient-to-r from-mamba-golden to-mamba-sky-blue bg-clip-text text-transparent">
              Adventure?
            </span>
          </h2>
          <p className="text-xl text-slate-200 mb-10 leading-relaxed font-inter">
            Join thousands of <span className="font-dancing italic text-mamba-golden">Mamba Hikers</span> exploring Kenya's most breathtaking
            trails. Your journey starts here.
          </p>
          <motion.button
            onClick={() => navigate("/programmes")}
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.98 }}
            className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue text-white text-lg font-montserrat font-bold rounded-2xl shadow-2xl hover:shadow-mamba-golden/50 transition-all duration-300"
          >
            Explore Training Programs
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </motion.button>
        </motion.div>
      </section>
    </ClientLayout>
  );
};

// Event Search Bar Component
const EventSearchBarRedesign = ({ handleSearch }) => {
  const [q, setQ] = useState();
  const [startDate, setStartDate] = useState();
  const [endDate, setEndDate] = useState();

  const handleSearchClicked = () => {
    let search = {};
    if (q !== null && q !== undefined) {
      search["q"] = q;
    }

    if (startDate !== null && startDate !== undefined) {
      search["startDate"] = startDate;
    }
    if (endDate !== null && endDate !== undefined) {
      search["endDate"] = endDate;
    }

    return handleSearch(search);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6 }}
      className="relative bg-white rounded-3xl border-2 border-mamba-sky-blue/20 shadow-lg hover:shadow-2xl hover:border-mamba-golden transition-all duration-300 overflow-hidden"
    >
      {/* Decorative top accent - Mamba Theme */}
      <div className="h-1 bg-gradient-to-r from-mamba-navy via-mamba-sky-blue to-mamba-golden" />

      <div className="p-8">
        {/* Header - Mamba Theme */}
        <div className="mb-6">
          <h3 className="text-2xl font-montserrat font-black text-mamba-navy tracking-tight mb-2">
            Find Your Next Adventure
          </h3>
          <p className="text-slate-600 text-sm font-inter">
            Search by destination, date, or explore all upcoming events
          </p>
        </div>

        {/* Search Input */}
        <div className="relative mb-4">
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
            <Search className="w-5 h-5" />
          </div>
          <input
            type="text"
            name="q"
            placeholder="Search events by destination..."
            className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-sky-blue focus:ring-4 focus:ring-mamba-sky-blue/20 transition-all duration-300 text-mamba-navy placeholder:text-slate-400"
            onChange={(text) => setQ(text?.target?.value)}
          />
        </div>

        {/* Date Filters */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          {/* Start Date */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              Start Date
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                <Calendar className="w-5 h-5" />
              </div>
              <input
                type="date"
                name="startDate"
                className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-sky-blue focus:ring-4 focus:ring-mamba-sky-blue/20 transition-all duration-300 text-mamba-navy"
                onChange={(date) => setStartDate(date?.target?.value)}
              />
            </div>
          </div>

          {/* End Date */}
          <div className="relative">
            <label className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">
              End Date
            </label>
            <div className="relative">
              <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-slate-400">
                <Calendar className="w-5 h-5" />
              </div>
              <input
                type="date"
                name="endDate"
                className="w-full pl-12 pr-4 py-4 border-2 border-mamba-sky-blue/30 rounded-2xl focus:outline-none focus:border-mamba-golden focus:ring-4 focus:ring-mamba-golden/20 transition-all duration-300 text-mamba-navy"
                onChange={(date) => setEndDate(date?.target?.value)}
              />
            </div>
          </div>
        </div>

        {/* Search Button - Mamba Theme */}
        <motion.button
          whileHover={{ scale: 1.02, y: -2 }}
          whileTap={{ scale: 0.98 }}
          className="w-full group relative px-6 py-4 bg-gradient-to-r from-mamba-navy to-mamba-sky-blue text-white font-montserrat font-bold rounded-2xl shadow-lg hover:shadow-mamba-sky-blue/50 transition-all duration-300 overflow-hidden"
          onClick={handleSearchClicked}
        >
          {/* Shine effect */}
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-1000" />

          <span className="relative flex items-center justify-center gap-2">
            <Search className="w-5 h-5" />
            Search Adventures
            <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
          </span>
        </motion.button>
      </div>
    </motion.div>
  );
};

// Event Card Component
const EventCardRedesign = ({
  adventure,
  index,
  handleClick,
  fadeInUp,
  getDifficultyColor,
}) => {
  return (
    <motion.div
      variants={fadeInUp}
      whileHover={{ y: -8 }}
      onClick={() => handleClick(adventure.slug)}
      className="group bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer border-2 border-mamba-sky-blue/20 hover:border-mamba-golden"
    >
      {/* Image */}
      <div className="relative h-64 overflow-hidden">
        <motion.img
          whileHover={{ scale: 1.1 }}
          transition={{ duration: 0.6 }}
          src={
            adventure?.destination_data?.images?.[0]?.image_url ||
            adventure?.display_image_url ||
            NoImage
          }
          alt={adventure.name}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-slate-900/70 via-slate-900/30 to-transparent" />

        {/* Difficulty Badge */}
        <div className="absolute top-4 right-4">
          <div
            className={`px-3 py-1.5 bg-gradient-to-r ${getDifficultyColor(
              adventure?.destination_data?.difficulty
            )} text-white text-xs font-bold rounded-full shadow-lg backdrop-blur-sm`}
          >
            {adventure?.destination_data?.difficulty || "Moderate"}
          </div>
        </div>

        {/* Distance Badge */}
        {adventure?.destination_data?.distance_in_km && (
          <div className="absolute bottom-4 left-4">
            <div className="px-3 py-1.5 bg-white/90 backdrop-blur-sm text-slate-900 text-xs font-bold rounded-full">
              {adventure?.destination_data?.distance_in_km} KM
            </div>
          </div>
        )}
      </div>

      {/* Content - Mamba Theme */}
      <div className="p-6">
        <div className="flex items-center gap-2 text-mamba-navy text-sm font-medium mb-2">
          <MapPin className="w-4 h-4 text-mamba-sky-blue" />
          {adventure?.destination_data?.name || "Amazing Location"}
        </div>
        <h3 className="text-xl font-montserrat font-bold text-mamba-navy mb-3 group-hover:text-mamba-sky-blue transition-colors line-clamp-2">
          {adventure?.name || adventure?.destination_data?.name}
        </h3>

        {/* Description */}
        <p className="text-sm text-slate-600 mb-4 line-clamp-2 leading-relaxed font-inter">
          {adventure?.destination_data?.description_no_tags ||
            adventure?.description ||
            "Join us for an unforgettable hiking experience."}
        </p>

        {/* Date & Price - Mamba Theme */}
        <div className="flex items-center justify-between pt-4 border-t border-mamba-sky-blue/20">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Calendar className="w-4 h-4 text-mamba-sky-blue" />
            <span className="font-medium">
              {new Date(adventure.start_date).toLocaleDateString("en-US", {
                month: "short",
                day: "numeric",
              })}
            </span>
          </div>
          {adventure.price && (
            <span className="text-lg font-montserrat font-bold text-mamba-navy">
              KSh {adventure.price?.toLocaleString()}
            </span>
          )}
          <button className="text-mamba-sky-blue font-semibold group-hover:gap-2 flex items-center gap-1 transition-all">
            Details
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Accent line at bottom - Mamba Theme */}
      <div className="h-1 bg-gradient-to-r from-mamba-golden to-mamba-sky-blue transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left" />
    </motion.div>
  );
};

export default EventListRedesign;
